import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const FINANCIAL_SERIES = [
  { period: "FY 2023", revenue: 41, compute: 433, operatingLoss: 551 },
  { period: "FY 2024", revenue: 386, compute: 2522, operatingLoss: 2980 },
  { period: "FY 2025", revenue: 4585, compute: 7326, operatingLoss: 8060 },
  { period: "H1 2026", revenue: 16286, compute: 8945, operatingLoss: 693 },
];

function money(value: number) {
  if (value >= 1000) return `$${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}B`;
  return `$${value}M`;
}

export default function AnthropicIpoFilingInfographic() {
  return (
    <section
      aria-labelledby="anthropic-filing-numbers-heading"
      className="not-prose container mx-auto mb-12 max-w-4xl px-4"
    >
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="border-b border-border bg-muted/30 px-5 py-5 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#008f78] dark:text-[#5ee6cb]">
            Filed financial context · not investment advice
          </p>
          <h2 id="anthropic-filing-numbers-heading" className="mt-1 font-serif text-xl text-foreground">
            Anthropic’s disclosed growth, compute cost and operating-loss trajectory
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Figures are in US$ millions from Anthropic’s 26 October 2026 preliminary S-1. H1 2026 is a six-month period and is not directly comparable with a full year. This chart reports filed historical figures; it does not estimate a share price, valuation or investment return.
          </p>
        </div>

        <div className="px-3 pb-1 pt-5 sm:px-5">
          <div className="h-[300px] sm:h-[360px]" role="img" aria-label="Bar chart comparing Anthropic revenue, compute and infrastructure expense, and operating loss across 2023 through the first half of 2026">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={FINANCIAL_SERIES} margin={{ top: 10, right: 12, bottom: 8, left: 8 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} tickFormatter={(value) => money(Number(value))} width={58} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted)/0.45)" }}
                  contentStyle={{ borderRadius: "12px", border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", color: "hsl(var(--foreground))" }}
                  formatter={(value, name) => [money(Number(value)), String(name)]}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#00A98F" radius={[4, 4, 0, 0]} />
                <Bar dataKey="compute" name="Compute & infrastructure" fill="#2457A7" radius={[4, 4, 0, 0]} />
                <Bar dataKey="operatingLoss" name="Operating loss" fill="#D85A3A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid gap-px border-t border-border bg-border sm:grid-cols-3">
          <div className="bg-card px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Revenue</p>
            <p className="mt-1 text-lg font-semibold text-foreground">$16.286B in H1 2026</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Up from $1.163B in H1 2025, according to the filing.</p>
          </div>
          <div className="bg-card px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Operating result</p>
            <p className="mt-1 text-lg font-semibold text-foreground">$693M loss in H1 2026</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">The operating loss narrowed year on year despite larger absolute costs.</p>
          </div>
          <div className="bg-card px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Net-loss bridge</p>
            <p className="mt-1 text-lg font-semibold text-foreground">$72.388B fair-value change</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">The S-1 separately identifies convertible-note, derivative and SAFE fair-value changes.</p>
          </div>
        </div>

        <div className="border-t border-border px-5 py-4 text-xs leading-relaxed text-muted-foreground sm:px-6">
          <p>
            <strong className="font-semibold text-foreground">Source:</strong>{" "}
            <a className="underline underline-offset-2 hover:text-[#008f78]" href="https://www.sec.gov/Archives/edgar/data/2130357/000162828026067168/anthropicpbc-sx1.htm" target="_blank" rel="noopener noreferrer">
              Anthropic, PBC Form S-1, filed 26 October 2026
            </a>{" "}
            (Consolidated Statements of Operations). The preliminary prospectus is subject to completion and the offering price and number of shares were blank at filing.
          </p>
        </div>
      </div>
    </section>
  );
}
